import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getRecordings } from 'api/canvms';
import { displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import type { Recording } from '../types/canvision.types';

export function useRecordings(siteId: number | null, cameraId: number | null, date: Date | null) {
  const dispatch = useDispatch();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchRecordings = useCallback(async (isRefresh = false) => {
    if (!siteId || !cameraId) {
      setRecordings([]);
      initialLoadDone.current = false;
      return;
    }

    try {
      // Only show loading on initial load, not refreshes
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);

      // Build date range for the selected day
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (date) {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        startDate = start.toISOString();
        endDate = end.toISOString();
      }

      const response = await getRecordings(siteId, cameraId, startDate, endDate);
      setRecordings(response.data?.recordings || []);
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch recordings';
      console.error('[CANVMS/fetchRecordings] Error fetching recordings:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [siteId, cameraId, date?.toDateString(), dispatch]);

  useEffect(() => {
    initialLoadDone.current = false;
    fetchRecordings(false);
  }, [fetchRecordings]);

  const refetch = useCallback(() => fetchRecordings(true), [fetchRecordings]);

  return { recordings, loading, error, refetch };
}
